
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function Landscape() {
  const services = [
    {
      id: 1,
      title: 'Garden Design & Installation',
      description: 'Custom garden designs that reflect your style and enhance your property',
      image: 'https://public.readdy.ai/ai/img_res/b35082dd7c339876a374839e7efbad48.jpg',
      features: ['Custom Design', 'Plant Selection', 'Seasonal Planning', 'Maintenance Guide']
    },
    {
      id: 2,
      title: 'Lawn Care & Maintenance',
      description: 'Keep your lawn healthy and beautiful year-round with our care services',
      image: 'https://readdy.ai/api/search-image?query=Perfect%20green%20lawn%20maintenance%2C%20lawn%20care%20service%2C%20healthy%20grass%2C%20professional%20landscaping%2C%20lawn%20mowing%2C%20garden%20maintenance%2C%20beautiful%20green%20yard&width=400&height=300&seq=17&orientation=landscape',
      features: ['Regular Mowing', 'Fertilization', 'Weed Control', 'Seasonal Care']
    },
    {
      id: 3,
      title: 'Hardscape & Outdoor Living',
      description: 'Create functional outdoor spaces with patios, walkways, and features',
      image: 'https://readdy.ai/api/search-image?query=Outdoor%20living%20space%20with%20patio%2C%20hardscape%20design%2C%20stone%20walkways%2C%20outdoor%20furniture%2C%20landscape%20architecture%2C%20backyard%20transformation%2C%20outdoor%20entertainment%20area&width=400&height=300&seq=18&orientation=landscape',
      features: ['Patio Installation', 'Walkway Design', 'Outdoor Kitchens', 'Water Features']
    }
  ];

  const benefits = [
    {
      icon: 'ri-home-heart-line',
      title: 'Increase Property Value',
      description: 'Professional landscaping can increase your property value by up to 15%'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Environmental Benefits',
      description: 'Improve air quality and create a healthier outdoor environment'
    },
    {
      icon: 'ri-heart-line',
      title: 'Enhanced Lifestyle',
      description: 'Create beautiful outdoor spaces for relaxation and entertainment'
    },
    {
      icon: 'ri-shield-line',
      title: 'Low Maintenance',
      description: 'Our designs focus on sustainable, low-maintenance solutions'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://readdy.ai/api/search-image?query=Beautiful%20landscape%20design%20with%20lush%20green%20grass%2C%20garden%20features%2C%20professional%20landscaping%2C%20outdoor%20spaces%2C%20natural%20beauty%2C%20landscape%20architecture%20background&width=1920&height=600&seq=19&orientation=landscape')`
        }}
      >
        <div className="text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">Landscape Services</h1>
          <p className="text-xl">Creating beautiful outdoor spaces that inspire and delight</p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Landscape Services</h2>
            <p className="text-xl text-gray-600">Transform your outdoor space into a beautiful oasis</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <i className="ri-check-line text-green-600 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Benefits of Professional Landscaping</h2>
            <p className="text-xl text-gray-600">Why invest in professional landscape design</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Transformation Gallery</h2>
            <p className="text-xl text-gray-600">See the amazing transformations we've created</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Backyard%20transformation%20before%20and%20after%2C%20landscape%20renovation%2C%20garden%20makeover%2C%20professional%20landscaping%20results%2C%20outdoor%20space%20improvement%2C%20beautiful%20garden%20design&width=600&height=400&seq=20&orientation=landscape"
                alt="Backyard Transformation"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Backyard Oasis</h3>
                <p className="text-gray-600">Complete backyard transformation with new lawn, garden beds, and patio area</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Front%20yard%20landscape%20design%2C%20curb%20appeal%20improvement%2C%20professional%20landscaping%2C%20beautiful%20front%20garden%2C%20home%20exterior%20enhancement%2C%20landscape%20architecture&width=600&height=400&seq=21&orientation=landscape"
                alt="Front Yard Design"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Front Yard Elegance</h3>
                <p className="text-gray-600">Enhanced curb appeal with sophisticated plantings and hardscape elements</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Year-Round Care</h2>
            <p className="text-xl text-gray-600">Seasonal services to keep your landscape beautiful</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-sun-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Spring</h3>
              <p className="text-gray-600">Cleanup, planting, and preparation for growing season</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Summer</h3>
              <p className="text-gray-600">Regular maintenance, watering, and pest control</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-maple-leaf-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fall</h3>
              <p className="text-gray-600">Leaf removal, winterization, and plant protection</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-snowy-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Winter</h3>
              <p className="text-gray-600">Snow removal, pruning, and planning for next year</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Transform Your Outdoor Space</h2>
          <p className="text-xl text-green-100 mb-8">
            Let us create the landscape of your dreams
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              Schedule Consultation
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
